// Match query and OCR tokens using the backend's Arabic spelling normalization.
// Original OCR text and polygons remain unchanged for display.
// Prefixes such as و stay attached: الحكومة does not match والحكومة in Exact mode.
export const searchTokens = (text = "") => text.normalize("NFKC")
  .replace(/[إأآٱ]/g, "ا")
  .replace(/ى/g, "ي")
  .replace(/ة/g, "ه")
  .replace(/[\u0610-\u061A\u064B-\u065F\u0670\u06D6-\u06ED\u0640\u200C\u200D]/g, "")
  .toLowerCase().match(/[\p{L}\p{N}]+/gu) || [];

const bounds = (polygon, page) => {
  const xs = polygon.map(p => p.x * page.width);
  const ys = polygon.map(p => p.y * page.height);
  return { left: Math.min(...xs), right: Math.max(...xs), top: Math.min(...ys), bottom: Math.max(...ys) };
};

const adjacent = (first, second, paragraph, page) => {
  if (first === second) return true;
  const a = bounds(first.polygon, page), b = bounds(second.polygon, page);
  const h = Math.max(a.bottom - a.top, b.bottom - b.top);
  const overlap = Math.min(a.bottom, b.bottom) - Math.max(a.top, b.top);
  const rtl = /[\u0600-\u06ff]/.test(first.text);
  if (overlap >= Math.min(a.bottom - a.top, b.bottom - b.top) * 0.4) {
    const gap = rtl ? a.left - b.right : b.left - a.right;
    return gap >= -h * 0.5 && gap <= h * 3;
  }
  // Wrapped phrases must follow an OCR line break within this paragraph and
  // move from the end of one line to the start of the next, not another column.
  if (!first.line_break || b.top <= a.top || b.top - a.bottom > h * 1.5) return false;
  const boxes = paragraph.words.map(w => bounds(w.polygon, page));
  const left = Math.min(...boxes.map(box => box.left));
  const right = Math.max(...boxes.map(box => box.right));
  return rtl
    ? a.left - left <= h * 2 && right - b.right <= h * 2
    : right - a.right <= h * 2 && b.left - left <= h * 2;
};

export const findOcrMatches = (data, query, mode = "any") => {
  const terms = searchTokens(query);
  if (!terms.length) return [];
  const matches = [];
  for (const page of data?.pages || []) {
    for (const paragraph of page.paragraphs) {
      const tokens = paragraph.words.flatMap(word => {
        const values = searchTokens(word.text);
        // Empty/duplicate words remain barriers for phrase matching.
        return (values.length ? values : [null]).map(value => ({ value, word }));
      });
      if (mode !== "exact") {
        // Any Word and All Words highlight individual terms. The backend
        // determines whether the document satisfies the selected match mode.
        const words = [...new Set(tokens.filter(token => terms.includes(token.value)).map(token => token.word))];
        words.forEach(word => matches.push({ pageIndex: page.page_index, text: word.text, polygons: [word.polygon] }));
        continue;
      }
      for (let start = 0; start <= tokens.length - terms.length; start++) {
        const hit = tokens.slice(start, start + terms.length);
        if (!hit.every((token, i) => token.value === terms[i])) continue;
        const words = [...new Set(hit.map(token => token.word))];
        if (!words.every((word, i) => i === 0 || adjacent(words[i - 1], word, paragraph, page))) continue;
        matches.push({ pageIndex: page.page_index, text: words.map(word => word.text).join(" "), polygons: words.map(word => word.polygon) });
      }
    }
  }
  return matches;
};

export const rotateOcrPoint = ({ x, y }, rotation) => {
  switch (((rotation % 360) + 360) % 360) {
    case 90: return { x: 1 - y, y: x };
    case 180: return { x: 1 - x, y: 1 - y };
    case 270: return { x: y, y: 1 - x };
    default: return { x, y };
  }
};

export const ocrFilename = (url) => {
  if (!url) return "";
  try {
    const path = new URL(url, window.location.origin).pathname;
    return decodeURIComponent(path.split("/").pop()).replace(/\.pdf$/i, "");
  } catch { return ""; }
};
